import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {useDesktop} from '../desktop/DesktopContext';

/* ===========================================================================
   Links between apps.
   ---------------------------------------------------------------------------
   "Tính khoản vay cho căn này" has to open the loan app AND arrive there with
   the price already filled in. Without a channel like this, either the apps
   start importing each other, or the shell grows a router — and a router that
   knows every screen's internals is the thing this shell exists to avoid.

   An intent is delivered once and then consumed, so reopening the loan app
   later does not re-apply a price the visitor has since changed.
   =========================================================================== */

export interface MarketIntent {
  listingId?: string;
  query?: string;
  /** Only listings with a 360 tour. */
  only360?: boolean;
}

export interface LoanIntent {
  price?: number;
  label?: string;
}

export interface ManageIntent {
  listingId?: string;
  create?: boolean;
  tab?: 'tin' | 'lien-he';
}

export interface AccountIntent {
  tab?: 'tai-khoan' | 'da-luu' | 'lien-he';
  /** Open straight on the sign-up form. */
  register?: boolean;
}

interface Entry {
  payload: unknown;
  seq: number;
}

interface LinkValue {
  entries: Record<string, Entry>;
  open: (appId: string, payload?: unknown) => void;
  consume: (appId: string, seq: number) => void;
}

const LinkContext = createContext<LinkValue | null>(null);

export function useAppLink(): {open: (appId: string, payload?: unknown) => void} {
  const value = useContext(LinkContext);
  if (!value) throw new Error('useAppLink must be used inside <AppLinkProvider>');
  return {open: value.open};
}

/** Runs `handler` once for each intent addressed to this app. */
export function useIntent<T>(appId: string, handler: (payload: T) => void): void {
  const value = useContext(LinkContext);
  const entry = value?.entries[appId];
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!value || !entry) return;
    handlerRef.current(entry.payload as T);
    value.consume(appId, entry.seq);
  }, [appId, entry, value]);
}

export function AppLinkProvider({children}: {children: ReactNode}) {
  const {openApp} = useDesktop();
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const seqRef = useRef(0);

  const open = useCallback(
    (appId: string, payload?: unknown) => {
      if (payload !== undefined) {
        seqRef.current += 1;
        const seq = seqRef.current;
        setEntries((prev) => ({...prev, [appId]: {payload, seq}}));
      }
      openApp(appId);
    },
    [openApp],
  );

  const consume = useCallback((appId: string, seq: number) => {
    setEntries((prev) => {
      // Only drop the exact intent that was handled: a second click while the
      // effect was in flight must not be swallowed.
      if (prev[appId]?.seq !== seq) return prev;
      const next = {...prev};
      delete next[appId];
      return next;
    });
  }, []);

  const value = useMemo<LinkValue>(() => ({consume, entries, open}), [consume, entries, open]);

  return <LinkContext.Provider value={value}>{children}</LinkContext.Provider>;
}
